import React, { useCallback, useEffect, useRef } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import AutoScroll from 'embla-carousel-auto-scroll'
import { useAutoScroll } from './EmblaCarouselAutoScroll'
import {
  NextButton,
  PrevButton,
  usePrevNextButtons
} from './EmblaCarouselArrowButtons'
import "./embla.css"
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";

const TWEEN_FACTOR_BASE = 0.2

const NightSkyEmblaCarousel = (props) => {
    const { slides, options } = props
    const [emblaRef, emblaApi] = useEmblaCarousel(options, [AutoScroll({speed: 1.5})])

    const tweenFactor = useRef(0)
    const tweenNodes = useRef([])
    const TWEEN_FACTOR_BASE = 0.2

    const setTweenNodes = useCallback((emblaApi) => {
        tweenNodes.current = emblaApi
        .slideNodes()
        .map((slideNode) => slideNode.querySelector('.embla__parallax__layer'))
    }, [])

    const setTweenFactor = useCallback((emblaApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length
    }, [])

    const tweenParallax = useCallback((emblaApi, event) => {
    const engine = emblaApi.internalEngine()
    const scrollProgress = emblaApi.scrollProgress()
    const slidesInView = emblaApi.slidesInView()
    const isScrollEvent = event?.type === 'scroll'

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress
      const slidesInSnap = engine.scrollSnapList[snapIndex]

      const slideIndex = engine.slideRegistry[snapIndex][0]
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target()

            if (slideIndex === loopItem.index && target !== 0) {
              diffToTarget =
                target < 0
                  ? scrollSnap - (1 + scrollProgress)
                  : scrollSnap + (1 - scrollProgress)
            }
          })
        }

        tweenNodes.current[slideIndex].style.transform = `translateX(${
          -100 * tweenFactor.current * diffToTarget
        }%)`
    })
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    setTweenNodes(emblaApi)
    setTweenFactor(emblaApi)
    tweenParallax(emblaApi)

    emblaApi
      .on('reInit', setTweenNodes)
      .on('reInit', setTweenFactor)
      .on('reInit', tweenParallax)
      .on('scroll', tweenParallax)
      .on('slideFocus', tweenParallax)
      .on('select', tweenParallax)
  }, [emblaApi, tweenParallax, setTweenNodes, setTweenFactor])

  useEffect(() => {
    Fancybox.bind("[data-fancybox]", {
      Toolbar: {
        display: {
          left: [],
          middle: ["zoomIn", "zoomOut"],
          right: ["close"],
        },
      },

      Carousel: {
        Navigation: false,
      },

      Images: {
        zoom: true,
      },

      dragToClose: true,
    });

    return () => {
      Fancybox.unbind("[data-fancybox]");
    };
  }, []);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi)

  const { autoScrollIsPlaying, toggleAutoScroll, onAutoScrollButtonClick } =
    useAutoScroll(emblaApi)

  return (
    <div className="embla" uk-scrollspy="cls: uk-animation-slide-bottom; repeat: false">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((index) => {
            const image = require(`./../../assets/nightsky/optimized/${index + 1}.jpg`)

            return(
            <div className="embla__slide" key={index}>
              <div className="embla__parallax">
                <div className="embla__parallax__layer" style={{borderRadius: "8px"}}>
                  <img
                    href={image}
                    data-fancybox={`image-${index}`}
                    className="embla__parallax__img"
                    src={image}
                    alt={`Night sky ${index + 1}`}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
            )
          })}
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton
            onClick={() => onAutoScrollButtonClick(onPrevButtonClick)}
            disabled={prevBtnDisabled}
          />
          <NextButton
            onClick={() => onAutoScrollButtonClick(onNextButtonClick)}
            disabled={nextBtnDisabled}
          />
        </div>

        <button
          className="embla__play"
          onClick={toggleAutoScroll}
          type="button"
        >
          {autoScrollIsPlaying ? (
            <svg className="embla__button__svg" viewBox="0 0 532 532">
              <path
                fill="currentColor"
                d="M128 64h96v384h-96zm160 0h96v384h-96z"
              />
            </svg>
          ) : (
            <svg className="embla__button__svg" viewBox="0 0 532 532">
              <path
                fill="currentColor"
                d="M128 64v384l320-192L128 64z"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  )
}

export default NightSkyEmblaCarousel
